import type {
  ComponentBaseContext,
  ComponentBaseMapper,
  ExecutionDetailsContext,
  ExecutionInfo,
  NodeInfo,
  OutputPayload,
  SubtitleContext,
} from "../../types";
import type { ComponentBaseProps, EventSection } from "@/ui/componentBase";
import type React from "react";
import { getBackgroundColorClass, getColorClass } from "@/lib/colors";
import { getState, getStateMap, getTriggerRenderer } from "../../mapperLookup";
import awsEcrIcon from "@/assets/icons/integrations/aws.ecr.svg";
import { renderTimeAgo } from "@/components/TimeAgo";
import { formatTimestampInUserTimezone } from "@/lib/timezone";
import type { MetadataItem } from "@/ui/metadataList";
import type {
  EcrImageScanFindings,
  EcrImageScanFindingsResponse,
  EcrRepositoryConfiguration,
  EcrRepositoryMetadata,
} from "./types";
import { getRepositoryLabel } from "./utils";
import { numberOrZero, stringOrDash } from "../../eventDisplay";

export const getImageScanFindingsMapper: ComponentBaseMapper = {
  props(context: ComponentBaseContext): ComponentBaseProps {
    const lastExecution = context.lastExecutions.length > 0 ? context.lastExecutions[0] : null;
    const componentName = context.componentDefinition.name || "unknown";

    return {
      title:
        context.node.name ||
        context.componentDefinition.label ||
        context.componentDefinition.name ||
        "Unnamed component",
      iconSrc: awsEcrIcon,
      iconColor: getColorClass(context.componentDefinition.color),
      collapsedBackground: getBackgroundColorClass(context.componentDefinition.color),
      collapsed: context.node.isCollapsed,
      eventSections: lastExecution ? getScanEventSections(context.nodes, lastExecution, componentName) : undefined,
      includeEmptyState: !lastExecution,
      metadata: getScanMetadataList(context.node),
      eventStateMap: getStateMap(componentName),
    };
  },

  getExecutionDetails(context: ExecutionDetailsContext): Record<string, string> {
    const outputs = context.execution.outputs as { default?: OutputPayload[] } | undefined;
    const result = outputs?.default?.[0]?.data as EcrImageScanFindingsResponse | undefined;

    if (!result) {
      return {};
    }

    return {
      ...scanImageDetails(result),
      ...scanFindingDetails(result.imageScanFindings),
    };
  },

  subtitle(context: SubtitleContext): string | React.ReactNode {
    if (!context.execution.createdAt) {
      return "";
    }
    return renderTimeAgo(new Date(context.execution.createdAt));
  },
};

function scanImageDetails(result: EcrImageScanFindingsResponse): Record<string, string> {
  return {
    Repository: stringOrDash(result.repositoryName),
    "Image Digest": stringOrDash(result.imageId?.imageDigest),
    "Image Tag": stringOrDash(result.imageId?.imageTag),
    "Scan Status": stringOrDash(result.imageScanStatus?.status),
    "Status Description": stringOrDash(result.imageScanStatus?.description),
  };
}

function scanFindingDetails(findings: EcrImageScanFindings | undefined): Record<string, string> {
  const counts = findings?.findingSeverityCounts || {};

  return {
    "Scan Completed At": findings?.imageScanCompletedAt
      ? formatTimestampInUserTimezone(findings.imageScanCompletedAt)
      : "-",
    "Vulnerability Source Updated At": findings?.vulnerabilitySourceUpdatedAt
      ? formatTimestampInUserTimezone(findings.vulnerabilitySourceUpdatedAt)
      : "-",
    "Findings Count": numberOrZero(findings?.findings?.length).toString(),
    Critical: numberOrZero(counts.CRITICAL).toString(),
    High: numberOrZero(counts.HIGH).toString(),
    Medium: numberOrZero(counts.MEDIUM).toString(),
    Low: numberOrZero(counts.LOW).toString(),
    Undefined: numberOrZero(counts.UNDEFINED).toString(),
  };
}

function getScanMetadataList(node: NodeInfo): MetadataItem[] {
  const metadata: MetadataItem[] = [];
  const nodeMetadata = node.metadata as EcrRepositoryMetadata | undefined;
  const configuration = node.configuration as EcrRepositoryConfiguration | undefined;

  const repositoryLabel = getRepositoryLabel(nodeMetadata, configuration);
  if (repositoryLabel) {
    metadata.push({ icon: "package", label: repositoryLabel });
  }

  return metadata;
}

function getScanEventSections(nodes: NodeInfo[], execution: ExecutionInfo, componentName: string): EventSection[] {
  const rootTriggerNode = nodes.find((n) => n.id === execution.rootEvent?.nodeId);
  const rootTriggerRenderer = getTriggerRenderer(rootTriggerNode?.componentName ?? "");
  const { title } = rootTriggerRenderer.getTitleAndSubtitle({ event: execution.rootEvent });

  return [
    {
      receivedAt: new Date(execution.createdAt!),
      eventTitle: title,
      eventSubtitle: renderTimeAgo(new Date(execution.createdAt!)),
      eventState: getState(componentName)(execution),
      eventId: execution.rootEvent!.id!,
    },
  ];
}
